import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { format } from "date-fns";
import {
  fetchDeposits,
  collectDepositPayment,
  refundDeposit,
} from "../../store/thunks/paymentsThunk";
import { setSelectedClinic } from "../../store/slices/clinicSlice";
import { fetchClinics, fetchClinicById } from "../../store/thunks/clinicThunk";
import { fetchInvoiceById } from "../../store/thunks/invoiceThunk";
import { fetchAppointmentById } from "../../store/thunks/appointmentsThunk";
import { clearCurrentInvoice } from "../../store/slices/invoiceSlice";
import { ToastContainer, toast } from "react-toastify";
import Table from "../../applicationUi/components/Table";
import ViewInvoicePDFModal from "../../modals/ViewInvoicePDFModal";
import DepositDetailsModal from "../../modals/DepositDetailsModal";
import DepositRefundModal from "../../applicationUi/components/DepositRefundModal";
import ReceiptGenerator from "../../applicationUi/components/ReceiptGenerator";

export default function ClientDeposits() {
  const dispatch = useDispatch();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [selectedDepositForRefund, setSelectedDepositForRefund] = useState(null);
  const [selectedDepositForReceipt, setSelectedDepositForReceipt] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [selectedClinicForDeposit, setSelectedClinicForDeposit] = useState(null);
  const [isLoadingClinic, setIsLoadingClinic] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    paymentMethod: "",
    paymentAmount: "",
    paymentDate: new Date().toISOString().split("T")[0],
    clinicId: "",
  });

  const client = useSelector((state) => state.clients.selectedClient);
  const user = useSelector((state) => state.auth.user);
  const selectedClinic = useSelector((state) => state.clinics.selectedClinic);
  const currentInvoice = useSelector((state) => state.invoices.currentInvoice);
  const clinics = useSelector((state) => state.clinics.clinics);
  
  // Check if user has refund permissions (owner or admin only)
  const hasRefundPermission = user && user.role && Array.isArray(user.role) && (
    user.role.includes('owner') || 
    user.role.includes('admin')
  );
  const paymentMethods = useMemo(() => {
    if (selectedClinicForDeposit?.paymentMethods && selectedClinicForDeposit.paymentMethods.length > 0) {
      const methods = selectedClinicForDeposit.paymentMethods;
      console.log('[Client Deposits] Using fetched clinic payment methods:', methods);
      return methods;
    }
    
    console.log('[Client Deposits] No payment methods available from selected clinic');
    // Return empty array when no clinic is selected or clinic has no payment methods
    return [];
  }, [selectedClinicForDeposit]);

  // Calculate deposit totals
  const depositTotals = useMemo(() => {
    if (!Array.isArray(deposits)) return { totalDeposits: 0, totalRefunds: 0, netDeposits: 0 };
    
    return deposits.reduce((totals, deposit) => {
      const depositAmount = parseFloat(deposit.amount) || 0;
      const refundAmount = deposit.refund ? (parseFloat(deposit.refund.refundAmount) || 0) : 0;
      
      // Use the backend-calculated depositBalance for consistency with individual deposit displays
      const availableBalance = parseFloat(deposit.depositBalance) || 0;
      
      return {
        totalDeposits: totals.totalDeposits + depositAmount,
        totalRefunds: totals.totalRefunds + refundAmount,
        netDeposits: totals.netDeposits + availableBalance
      };
    }, { totalDeposits: 0, totalRefunds: 0, netDeposits: 0 });
  }, [deposits]);

  const enrichDepositsWithClinicInfo = async (deposits) => {
    console.log("[Client Deposits] Starting enrichment for", deposits.length, "deposits");
    console.log("[Client Deposits] Client data:", client);
    console.log("[Client Deposits] Client clinic info:", client?.clinic);
    console.log("[Client Deposits] Available clinics from Redux:", clinics);
    
    const enrichedDeposits = [];
    
    for (const deposit of deposits) {
      let enrichedDeposit = { ...deposit };
      
      // If deposit doesn't have clinic info, try to get it
      if (!deposit.clinic) {
        console.log("[Client Deposits] Deposit missing clinic info:", deposit._id);
        
        // Try multiple fallback strategies
        let fallbackClinic = null;
        
        // Strategy 1: Use client's populated clinic
        if (client?.clinic?.name) {
          fallbackClinic = client.clinic;
          console.log("[Client Deposits] Using client populated clinic:", client.clinic.name);
        }
        // Strategy 2: Look up client's clinic ID in Redux clinics
        else if (client?.clinic && clinics.length > 0) {
          const clientClinicId = typeof client.clinic === 'string' ? client.clinic : client.clinic._id;
          fallbackClinic = clinics.find(clinic => clinic._id === clientClinicId);
          if (fallbackClinic) {
            console.log("[Client Deposits] Found client clinic in Redux:", fallbackClinic.name);
          }
        }
        // Strategy 3: If client has a clinic field but it's just an ID, look it up
        else if (typeof client?.clinic === 'string' && clinics.length > 0) {
          fallbackClinic = clinics.find(clinic => clinic._id === client.clinic);
          if (fallbackClinic) {
            console.log("[Client Deposits] Found client clinic by ID lookup:", fallbackClinic.name);
          }
        }
        
        if (fallbackClinic) {
          enrichedDeposit.clinic = fallbackClinic;
        } else {
          console.log("[Client Deposits] No clinic fallback available");
        }
      } else {
        console.log("[Client Deposits] Deposit has clinic info:", deposit.clinic);
      }
      
      enrichedDeposits.push(enrichedDeposit);
    }
    
    console.log("[Client Deposits] Enrichment complete, returning", enrichedDeposits.length, "deposits");
    return enrichedDeposits;
  };

  const loadDeposits = async () => {
    if (!client?._id) return;
    try {
      const response = await dispatch(fetchDeposits(client._id)).unwrap();
      console.log("[Client Deposits] API Response:", response);
      console.log("[Client Deposits] Deposits data:", response.deposits);
      
      if (response.deposits && response.deposits.length > 0) {
        console.log("[Client Deposits] First deposit:", response.deposits[0]);
        console.log("[Client Deposits] First deposit clinic:", response.deposits[0].clinic);
        if (response.deposits[0].depositUsage && response.deposits[0].depositUsage.length > 0) {
          console.log("[Client Deposits] First usage:", response.deposits[0].depositUsage[0]);
          console.log("[Client Deposits] Invoice data:", response.deposits[0].depositUsage[0].invoice);
        }
      }
      
      const depositsArray = Array.isArray(response.deposits) ? response.deposits : [];
      const enrichedDeposits = await enrichDepositsWithClinicInfo(depositsArray);
      
      setDeposits(enrichedDeposits);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch deposits");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (client?._id) {
      loadDeposits();
    }
  }, [client, dispatch]);

  // Re-enrich deposits when clinics data becomes available
  useEffect(() => {
    if (deposits.length > 0 && clinics.length > 0) {
      const reEnrichDeposits = async () => {
        console.log("[Client Deposits] Re-enriching deposits with clinic data");
        const enrichedDeposits = await enrichDepositsWithClinicInfo(deposits);
        setDeposits(enrichedDeposits);
      };
      reEnrichDeposits();
    }
  }, [clinics]);

  // Load clinics on component mount
  useEffect(() => {
    dispatch(fetchClinics());
  }, [dispatch]);

  // Set default clinic for the deposit (client's clinic if available)
  useEffect(() => {
    if (client?.clinic?._id && !paymentDetails.clinicId) {
      setPaymentDetails(prev => ({
        ...prev,
        clinicId: client.clinic._id
      }));
    }
  }, [client?.clinic?._id, paymentDetails.clinicId]);

  // Fetch clinic data when clinicId changes
  useEffect(() => {
    const fetchClinicData = async () => {
      if (!paymentDetails.clinicId) {
        setSelectedClinicForDeposit(null);
        return;
      }

      setIsLoadingClinic(true);
      try {
        console.log('[Client Deposits] Fetching clinic data for:', paymentDetails.clinicId);
        const clinicData = await dispatch(fetchClinicById(paymentDetails.clinicId)).unwrap();
        console.log('[Client Deposits] Fetched clinic data:', clinicData);
        setSelectedClinicForDeposit(clinicData);
      } catch (error) {
        console.error('[Client Deposits] Failed to fetch clinic data:', error);
        setSelectedClinicForDeposit(null);
        toast.error('Failed to load clinic payment methods');
      } finally {
        setIsLoadingClinic(false);
      }
    };

    fetchClinicData();
  }, [paymentDetails.clinicId, dispatch]);


  const handleCollectDeposit = async () => {
    if (!paymentDetails.clinicId) {
      toast.error("Please select a clinic for this deposit");
      return;
    }
    
    if (!paymentDetails.paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }
    
    if (!paymentDetails.paymentAmount || Number(paymentDetails.paymentAmount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    
    try {
      const response = await dispatch(
        collectDepositPayment({
          paymentMethod: paymentDetails.paymentMethod,
          paymentAmount: Number(paymentDetails.paymentAmount),
          paymentDate: paymentDetails.paymentDate,
          createdBy: user._id,
          clientId: client._id,
          clinicId: paymentDetails.clinicId,
        })
      ).unwrap();

      if (response.payment) {
        setShowCollectModal(false);
        setSelectedClinicForDeposit(null);
        await loadDeposits();
        toast.success(response.message || "Deposit collected successfully");
      }
    } catch (error) {
      console.error("Error collecting deposit:", error);
      toast.error("Failed to collect deposit. Please try again.");
    }
  };

  const handleInvoiceGeneratorClose = () => {
    setShowInvoiceGenerator(false);
    dispatch(clearCurrentInvoice());
  };

  const handleDepositModalClose = () => {
    setShowDepositModal(false);
    setSelectedDeposit(null);
  };

  const handleInvoiceClickFromDeposit = async (invoice) => {
    if (!invoice || !invoice._id) {
      console.error("Invalid invoice data for click");
      return;
    }
    
    handleDepositModalClose();
    
    setIsLoadingInvoice(true);
    try {
      const invoiceData = await dispatch(fetchInvoiceById(invoice._id)).unwrap();
      if (invoiceData.appointmentId) {
        await dispatch(fetchAppointmentById(invoiceData.appointmentId));
      }
      setShowInvoiceGenerator(true);
    } catch (error) {
      console.error("Error loading invoice from deposit:", error);
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const handleDepositClick = (deposit) => {
    setSelectedDeposit(deposit);
    setShowDepositModal(true);
  };

  const handleInvoiceClick = async (usage) => {
    if (!usage?.invoice?._id) {
      console.error("Invalid invoice data for click");
      return;
    }
    
    setIsLoadingInvoice(true);
    try {
      const invoiceData = await dispatch(fetchInvoiceById(usage.invoice._id)).unwrap();
      if (invoiceData.appointmentId) {
        await dispatch(fetchAppointmentById(invoiceData.appointmentId));
      }
      setShowInvoiceGenerator(true);
    } catch (error) {
      console.error("Error loading invoice details:", error);
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const handleRefundDeposit = (deposit) => {
    if (!deposit.depositBalance || deposit.depositBalance <= 0) {
      toast.error("This deposit has no available balance to refund");
      return;
    }
    
    setSelectedDepositForRefund(deposit);
    setShowRefundModal(true);
  };

  const handleDepositRefundConfirm = async (refundData) => {
    try {
      console.log(`[Deposit Refund] Processing deposit refund:`, {
        deposit: selectedDepositForRefund,
        refundData
      });

      await dispatch(refundDeposit({
        depositId: selectedDepositForRefund._id,
        refundAmount: refundData.refundAmount,
        paymentMethod: refundData.paymentMethod,
        reason: refundData.reason
      })).unwrap();

      toast.success(`Deposit refund of $${refundData.refundAmount.toFixed(2)} processed successfully`);

      // Refresh deposits data
      await loadDeposits();

    } catch (error) {
      console.error(`[Deposit Refund] Error processing deposit refund:`, error);

      // Handle specific error cases
      if (error.error && error.error.includes('privileges')) {
        toast.error("You don't have permission to refund deposits. Only owners and administrators can perform refunds.");
      } else {
        toast.error(error.error || "Failed to process deposit refund");
      }
    }
  };

  const handleViewReceipt = async (deposit) => {
    console.log(`[Client Deposits] Opening receipt for deposit ${deposit._id}`);

    try {
      // Ensure we have full clinic data
      let fullClinicData = deposit.clinic;

      // If clinic is just an ID or missing address/phone data, fetch full clinic info
      if (!deposit.clinic || typeof deposit.clinic === 'string' || !deposit.clinic.address || !deposit.clinic.phoneNumber) {
        console.log(`[Client Deposits] Fetching full clinic data for deposit`);
        const clinicId = typeof deposit.clinic === 'string' ? deposit.clinic : deposit.clinic?._id;

        if (clinicId) {
          fullClinicData = await dispatch(fetchClinicById(clinicId)).unwrap();
          console.log(`[Client Deposits] Fetched clinic data:`, fullClinicData);
        } else {
          console.warn(`[Client Deposits] No clinic ID available for deposit ${deposit._id}`);
        }
      }

      // Create enriched deposit with full clinic data
      const enrichedDeposit = {
        ...deposit,
        clinic: fullClinicData
      };

      setSelectedDepositForReceipt(enrichedDeposit);
      setShowReceiptModal(true);
    } catch (error) {
      console.error(`[Client Deposits] Error fetching clinic data for receipt:`, error);
      toast.error("Failed to load clinic information. Please try again.");
    }
  };

  const handleCloseReceipt = () => {
    console.log(`[Client Deposits] Closing receipt modal`);
    setShowReceiptModal(false);
    setSelectedDepositForReceipt(null);
  };

  if (loading) {
    return <div className="p-6">Loading deposits...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500">{error}</div>;
  }

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 space-y-6">
      <ToastContainer />
      {/* Summary Section */}
      {Array.isArray(deposits) && deposits.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-600">Total Deposits</div>
            <div className="text-2xl font-bold text-blue-900">${depositTotals.totalDeposits.toFixed(2)}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm font-medium text-red-600">Total Refunds</div>
            <div className="text-2xl font-bold text-red-900">${depositTotals.totalRefunds.toFixed(2)}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm font-medium text-green-600">Net Available</div>
            <div className="text-2xl font-bold text-green-900">${depositTotals.netDeposits.toFixed(2)}</div>
          </div>
        </div>
      )}
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Client Deposits</h2>
        <button
          onClick={() => {
            // Reset payment details when opening modal
            setPaymentDetails({
              paymentMethod: "",
              paymentAmount: "",
              paymentDate: new Date().toISOString().split("T")[0],
              clinicId: client?.clinic?._id || "",
            });
            setSelectedClinicForDeposit(null);
            setShowCollectModal(true);
          }}
          className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-indigo-600 text-white hover:bg-indigo-500 h-9 px-4 py-2 shadow-sm">
          Collect Deposit
        </button>
      </div>

      {/* Deposits List */}
      {!Array.isArray(deposits) || deposits.length === 0 ? (
        <div className="text-sm text-gray-500">No deposits found for this client.</div>
      ) : (
        <div className="space-y-4">
          {deposits.map((deposit) => (
            <div
              key={deposit?._id || Math.random()}
              className="bg-gradient-to-r from-white to-blue-50/30 rounded-lg shadow-sm border border-gray-200 p-5 space-y-4 hover:shadow-md transition-shadow duration-200">
              <div 
                className="flex justify-between items-center cursor-pointer hover:bg-blue-50 -m-2 p-3 rounded-lg transition-all duration-200 border-2 border-transparent hover:border-blue-200"
                onClick={() => handleDepositClick(deposit)}
                title="Click to view deposit details">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-700">Deposit Amount:</span>
                      <span className="text-lg font-semibold text-green-600">
                        ${deposit?.amount?.toFixed(2) || "0.00"}
                      </span>
                      {deposit?.clinic?.name ? (
                        <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                          {deposit.clinic.name}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                          Unknown Clinic
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {deposit?.paymentDate
                        ? (() => {
                            try {
                              const date = new Date(deposit.paymentDate);
                              // Use UTC methods to get the intended date components
                              const year = date.getUTCFullYear();
                              const month = date.getUTCMonth();
                              const day = date.getUTCDate();
                              // Create a new date in local timezone with the UTC date components
                              const localDate = new Date(year, month, day);
                              return format(localDate, "MMM dd, yyyy");
                            } catch (error) {
                              console.error('[Client Deposits] Date formatting error:', error, 'for date:', deposit.paymentDate);
                              return "Invalid Date";
                            }
                          })()
                        : "N/A"} • {deposit?.paymentMethod?.charAt(0).toUpperCase() + deposit?.paymentMethod?.slice(1) || 'Unknown'}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Available:</span>
                    <span className={`text-lg font-semibold ${
                      (deposit?.depositBalance || 0) > 0 ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      ${deposit?.depositBalance?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  {/* Show refund indicator if deposit has refund information */}
                  {deposit?.refund && (
                    <div className="space-y-1 mt-1">
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-red-600 font-medium">
                          Refunded ${deposit.refund.refundAmount?.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {deposit.refund.refundDate 
                          ? (() => {
                              try {
                                const date = new Date(deposit.refund.refundDate);
                                const year = date.getUTCFullYear();
                                const month = date.getUTCMonth();
                                const day = date.getUTCDate();
                                const localDate = new Date(year, month, day);
                                return format(localDate, "MMM dd, yyyy");
                              } catch (error) {
                                return "Invalid Date";
                              }
                            })()
                          : "N/A"} • {deposit.refund.refundMethod?.charAt(0).toUpperCase() + deposit.refund.refundMethod?.slice(1) || 'Unknown'}
                      </div>
                      {deposit.refund.refundedBy && (
                        <div className="text-xs text-gray-400">
                          by {deposit.refund.refundedBy.firstName} {deposit.refund.refundedBy.lastName}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent deposit detail modal from opening
                        handleViewReceipt(deposit);
                      }}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      View Receipt
                    </button>
                    {hasRefundPermission && (deposit?.depositBalance || 0) > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent deposit detail modal from opening
                          handleRefundDeposit(deposit);
                        }}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 border border-orange-200 rounded hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                      >
                        <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                        </svg>
                        Refund
                      </button>
                    )}
                    <div className="text-xs text-blue-600 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      View details
                    </div>
                  </div>
                </div>
              </div>
              {deposit?.depositUsage && deposit.depositUsage.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900">Usage History</h3>
                    <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                      {deposit.depositUsage.length} application{deposit.depositUsage.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {deposit.depositUsage.map((usage, index) => {
                      const usageAmount = usage?.amountUsed || 0;
                      const invoiceAmount = usage?.invoice?.amount || 0;
                      
                      return (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all duration-200"
                          onClick={() => handleInvoiceClick(usage)}
                          title="Click to view invoice details">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <span className="text-sm font-semibold text-blue-700 hover:text-blue-800">
                                    Invoice {usage?.invoice?.invoiceId || "N/A"}
                                  </span>
                                </div>
                                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                  {usage?.invoice?.status || 'Applied'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600">
                                Applied on {usage?.dateUsed ? format(new Date(usage.dateUsed), "MMM dd, yyyy") : "N/A"}
                                {usage?.invoice?.serviceDate && (
                                  <span className="mx-2">•</span>
                                )}
                                {usage?.invoice?.serviceDate && (
                                  <span>Service: {format(new Date(usage.invoice.serviceDate), "MMM dd, yyyy")}</span>
                                )}
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-lg font-semibold text-orange-600">
                                -${usageAmount.toFixed(2)}
                              </div>
                              {invoiceAmount > 0 && (
                                <div className="text-xs text-gray-500">
                                  of ${invoiceAmount.toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center text-xs text-blue-600">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            Click to view invoice details
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Collect Deposit Modal */}
      {showCollectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity"
            onClick={() => setShowCollectModal(false)}></div>
          <div className="bg-white rounded-sm shadow-lg border border-gray-200 max-w-lg w-full relative z-10">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Collect Deposit</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Clinic
                </label>
                <select
                  className="flex h-9 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={paymentDetails.clinicId}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      clinicId: e.target.value,
                      paymentMethod: "", // Reset payment method when clinic changes
                    })
                  }>
                  <option value="">Select a clinic</option>
                  {clinics.map((clinic) => (
                    <option key={clinic._id} value={clinic._id}>
                      {clinic.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Payment Method
                </label>
                <select
                  className="flex h-9 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={paymentDetails.paymentMethod}
                  disabled={isLoadingClinic || !paymentDetails.clinicId}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      paymentMethod: e.target.value,
                    })
                  }>
                  {isLoadingClinic ? (
                    <option value="">Loading payment methods...</option>
                  ) : !paymentDetails.clinicId ? (
                    <option value="">Select a clinic first</option>
                  ) : paymentMethods.length === 0 ? (
                    <option value="">No payment methods configured for this clinic</option>
                  ) : (
                    <>
                      <option value="">Select payment method</option>
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.englishDescription}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Amount
                </label>
                <input
                  type="number"
                  className="flex h-9 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={paymentDetails.paymentAmount}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      paymentAmount: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Payment Date
                </label>
                <input
                  type="date"
                  className="flex h-9 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={paymentDetails.paymentDate}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      paymentDate: e.target.value,
                    })
                  }
                />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleCollectDeposit}
                  className="inline-flex items-center justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-indigo-600 text-white hover:bg-indigo-500 h-9 px-4 py-2 shadow-sm">
                  Collect
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCollectModal(false);
                    setSelectedClinicForDeposit(null);
                  }}
                  className="inline-flex items-center justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-100 text-gray-900 hover:bg-gray-200 h-9 px-4 py-2 shadow-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modals */}
      {showInvoiceGenerator && currentInvoice && (
        <ViewInvoicePDFModal
          onClose={handleInvoiceGeneratorClose}
        />
      )}
      {showDepositModal && selectedDeposit && (
        <DepositDetailsModal
          deposit={selectedDeposit}
          onClose={handleDepositModalClose}
          onInvoiceClick={handleInvoiceClickFromDeposit}
        />
      )}
      
      {/* Deposit Refund Modal */}
      {showRefundModal && selectedDepositForRefund && (
        <DepositRefundModal
          isOpen={showRefundModal}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedDepositForRefund(null);
          }}
          onConfirm={handleDepositRefundConfirm}
          deposit={selectedDepositForRefund}
        />
      )}

      {/* Receipt Generator Modal */}
      {showReceiptModal && selectedDepositForReceipt && (
        <ReceiptGenerator
          deposit={selectedDepositForReceipt}
          client={client}
          onClose={handleCloseReceipt}
        />
      )}
    </div>
  );
}
